import React, { useEffect, useMemo, useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../components/api/axiosInstance";
import { fetchSubTasksByTaskId } from "../subTaskService";
import { fetchTasksByProject } from "../taskService";
import {
  createIssue,
  extractCreatedIssueId,
  fetchAllUsers,
  fetchIssueStatuses,
  uploadIssueFile
} from "./issueService";
import { fetchTestCases } from "./testCaseService";
import { ISSUE_CREATE_FORM_DRAFT_KEY } from "./issueCreateFormDraft";

const ISSUE_TYPES = ["Bug", "Task", "Improvement"];
const ISSUE_LAYERS = ["Frontend", "Backend", "Database", "API", "DevOps", "Other"];
const ISSUE_PRIORITIES = ["High", "Medium", "Low"];

const FALLBACK_STATUSES = [
  { id: 1, name: "Open" },
  { id: 2, name: "In Progress" },
  { id: 3, name: "Closed" }
];

export default function IssueCreateForm({ onCreated, canCreate = true }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAssigneesDropdown, setShowAssigneesDropdown] = useState(false);

  const [statuses, setStatuses] = useState(FALLBACK_STATUSES);
  const [loadingStatuses, setLoadingStatuses] = useState(true);

  const [subtasks, setSubtasks] = useState([]);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);

  const [testCases, setTestCases] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);

  const [pendingFiles, setPendingFiles] = useState([]);

  const [formData, setFormData] = useState({
    projectId: "",
    subProjectId: "",
    taskId: "",
    subTaskId: "",
    title: "",
    description: "",
    type: "Bug",
    issueLayer: "Backend",
    // Default status should be "Open"
    statusId: String(FALLBACK_STATUSES.find((s) => s.name === "Open")?.id ?? 1),
    priority: "Low",
    dueDate: "",
    actualEndDate: "",
    assignedUserIds: [],
    testCaseId: ""
  });

  const [statusTouched, setStatusTouched] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ISSUE_CREATE_FORM_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.formData && typeof draft.formData === "object") {
        setFormData((prev) => ({ ...prev, ...draft.formData }));
        if (typeof draft.statusTouched === "boolean") {
          setStatusTouched(draft.statusTouched);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoadingProjects(true);
      try {
        const res = await axiosInstance.get("/projects");
        const raw = res.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? raw?.projects ?? [];
        setProjects(arr || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!formData.projectId) {
      setSubprojects([]);
      return;
    }
    let cancelled = false;
    setLoadingSubprojects(true);
    axiosInstance
      .get(`/sub-projects/project/${formData.projectId}`)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data;
        let arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        const mapped = (arr || [])
          .map((sp) => ({
            id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
            name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
          }))
          .filter((x) => x.id != null && String(x.name).trim() !== "");
        setSubprojects(mapped);
        if (mapped.length === 0) {
          setFormData((f) => ({ ...f, subProjectId: "" }));
        }
      })
      .catch(() => {
        if (!cancelled) setSubprojects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubprojects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.projectId]);

  const showSubprojectField =
    Boolean(formData.projectId) && (loadingSubprojects ? true : subprojects.length > 0);

  const showSubTaskField =
    Boolean(formData.taskId) && (loadingSubtasks ? true : subtasks.length > 0);

  useEffect(() => {
    if (!formData.projectId) {
      setTasks([]);
      return;
    }
    if (loadingSubprojects) {
      setTasks([]);
      return;
    }

    let cancelled = false;
    const loadTasks = async () => {
      setLoadingTasks(true);
      try {
        const subId =
          showSubprojectField && formData.subProjectId ? formData.subProjectId : null;
        const list = await fetchTasksByProject(formData.projectId, subId);
        if (cancelled) return;
        const mapped = (list || [])
          .map((t) => ({
            id: t.Task_Id ?? t.task_id ?? t.id,
            title: t.Title ?? t.title ?? t.Task_Title ?? t.task_title ?? "—"
          }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setTasks(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error("Error loading tasks:", err);
          setTasks([]);
        }
      } finally {
        if (!cancelled) setLoadingTasks(false);
      }
    };
    loadTasks();
    return () => {
      cancelled = true;
    };
  }, [formData.projectId, formData.subProjectId, loadingSubprojects, showSubprojectField]);

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const list = await fetchAllUsers();
        const sorted = (list || []).sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setUsers(sorted);
      } catch (err) {
        console.error("Error loading users:", err);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const loadStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const list = await fetchIssueStatuses();
        const mapped = (list || [])
          .map((s) => ({ id: s.id, name: s.name, description: s.description }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setStatuses(mapped.length ? mapped : FALLBACK_STATUSES);
      } catch (err) {
        console.error("Error loading issue statuses:", err);
        setStatuses(FALLBACK_STATUSES);
      } finally {
        setLoadingStatuses(false);
      }
    };
    loadStatuses();
  }, []);

  // Once statuses are loaded, ensure "Open" is selected by default (unless user changed it).
  useEffect(() => {
    if (statusTouched) return;
    if (!Array.isArray(statuses) || statuses.length === 0) return;

    const openStatus = statuses.find((s) => String(s?.name ?? "").trim().toLowerCase() === "open");
    if (!openStatus || openStatus.id == null) return;

    setFormData((prev) => ({ ...prev, statusId: String(openStatus.id) }));
  }, [statuses, statusTouched]);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest(".form-group-multi-select")) {
        setShowAssigneesDropdown(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, [showAssigneesDropdown]);

  useEffect(() => {
    const loadSubtasks = async () => {
      const taskIdNum = Number(formData.taskId);
      if (!taskIdNum || !Number.isFinite(taskIdNum)) {
        setSubtasks([]);
        setFormData((prev) => ({ ...prev, subTaskId: "" }));
        return;
      }
      setLoadingSubtasks(true);
      try {
        const list = await fetchSubTasksByTaskId(taskIdNum);
        const mapped = (list || [])
          .map((st) => ({
            id: st.Sub_Task_Id ?? st.sub_task_id ?? st.id,
            title: st.Title ?? st.title ?? "—"
          }))
          .filter((x) => x.id != null);
        setSubtasks(mapped);
        if (mapped.length === 0) {
          setFormData((prev) => ({ ...prev, subTaskId: "" }));
        }
      } catch (err) {
        console.error("Error loading subtasks:", err);
        setSubtasks([]);
        setFormData((prev) => ({ ...prev, subTaskId: "" }));
      } finally {
        setLoadingSubtasks(false);
      }
    };
    loadSubtasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.taskId]);

  // Load test cases when Task/Sub Task changes
  useEffect(() => {
    const taskIdNum = Number(formData.taskId);
    if (!taskIdNum || !Number.isFinite(taskIdNum)) {
      setTestCases([]);
      return;
    }

    const subTaskIdNum = formData.subTaskId ? Number(formData.subTaskId) : null;

    let cancelled = false;
    setLoadingTestCases(true);

    fetchTestCases({ taskId: taskIdNum, subTaskId: subTaskIdNum })
      .then((list) => {
        if (cancelled) return;
        const mapped = (list || [])
          .map((tc) => ({
            id: tc.Test_Case_Id ?? tc.test_case_id ?? tc.id,
            title:
              tc.Title ??
              tc.title ??
              tc.Test_Case_Title ??
              tc.test_case_title ??
              "—",
          }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setTestCases(mapped);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error loading test cases:", err);
        setTestCases([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingTestCases(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formData.taskId, formData.subTaskId]);

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.assignedUserIds.length === users.length;

  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => ({
      ...prev,
      assignedUserIds: allUsersSelected ? [] : users.map((u) => u.id)
    }));
  };

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.assignedUserIds.includes(userId)
        ? prev.assignedUserIds.filter((id) => id !== userId)
        : [...prev.assignedUserIds, userId];
      return { ...prev, assignedUserIds: ids };
    });
  };

  const statusOptions = useMemo(() => statuses, [statuses]);

  const persistIssueDraftAndOpenCreateTestCase = () => {
    if (!canCreate || !formData.taskId) return;
    try {
      sessionStorage.setItem(
        ISSUE_CREATE_FORM_DRAFT_KEY,
        JSON.stringify({ formData, statusTouched })
      );
    } catch {
      /* ignore */
    }
    navigate("/issues/test-cases/add", {
      state: { returnTo: "/issues/add", fromIssueCreate: true }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "projectId") {
        return {
          ...prev,
          projectId: value,
          subProjectId: "",
          taskId: "",
          subTaskId: "",
          dueDate: "",
          actualEndDate: "",
          testCaseId: ""
        };
      }
      if (name === "subProjectId") {
        return {
          ...prev,
          subProjectId: value,
          taskId: "",
          subTaskId: "",
          dueDate: "",
          actualEndDate: "",
          testCaseId: ""
        };
      }
      if (name === "taskId") {
        return { ...prev, taskId: value, subTaskId: "", dueDate: "", actualEndDate: "", testCaseId: "" };
      }
      if (name === "subTaskId") {
        return { ...prev, subTaskId: value, dueDate: "", actualEndDate: "", testCaseId: "" };
      }
      if (name === "statusId") {
        setStatusTouched(true);
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) return;

    const taskId = Number(formData.taskId);
    const subTaskId = formData.subTaskId ? Number(formData.subTaskId) : null;
    const statusId = Number(formData.statusId);
    const title = (formData.title || "").trim();
    const description = (formData.description || "").trim();
    const testCaseIdNum = Number(formData.testCaseId);
    const testCaseIds =
      formData.testCaseId && Number.isFinite(testCaseIdNum) && testCaseIdNum > 0
        ? [testCaseIdNum]
        : [];

    const projectId = Number(formData.projectId);
    if (!projectId || !Number.isFinite(projectId)) {
      setError("Project is required.");
      return;
    }
    if (!taskId || !title) {
      setError("Task and Title are required.");
      return;
    }
    if (!statusId) {
      setError("Status is required.");
      return;
    }

    setError("");
    const payload = {
      Task_Id: taskId,
      Sub_Task_Id: subTaskId,
      Title: title,
      Description: description || null,
      Type: formData.type || "Bug",
      Issue_Layer: formData.issueLayer || "Backend",
      Status_Id: statusId,
      Priority: formData.priority || "Low",
      Due_Date: (formData.dueDate || "").trim(),
      Actual_End_Date: (formData.actualEndDate || "").trim(),
      Assigned_User_Ids: (formData.assignedUserIds || []).map((id) => Number(id)),
      Test_Case_Ids: testCaseIds
    };

    setSubmitting(true);
    try {
      const created = await createIssue(payload);
      const newIssueId = extractCreatedIssueId(created);

      if (pendingFiles.length > 0) {
        if (newIssueId == null) {
          setError(
            "Issue was created, but the response did not include an issue id, so attachments were not uploaded. Add them from View or Edit issue."
          );
          setPendingFiles([]);
          try {
            sessionStorage.removeItem(ISSUE_CREATE_FORM_DRAFT_KEY);
          } catch {
            /* ignore */
          }
          if (onCreated) onCreated(created, { issueId: null });
          setFormData((prev) => ({
            ...prev,
            projectId: "",
            subProjectId: "",
            taskId: "",
            subTaskId: "",
            title: "",
            description: "",
            dueDate: "",
            actualEndDate: "",
            assignedUserIds: [],
            testCaseId: ""
          }));
          return;
        }
        for (const file of pendingFiles) {
          try {
            await uploadIssueFile(newIssueId, file);
          } catch (uploadErr) {
            console.error("Issue file upload after create:", uploadErr);
            setError(
              uploadErr.response?.data?.message ||
                uploadErr.response?.data?.error ||
                "Issue created, but a file failed to upload. Add files from the issue page."
            );
            setPendingFiles([]);
            try {
              sessionStorage.removeItem(ISSUE_CREATE_FORM_DRAFT_KEY);
            } catch {
              /* ignore */
            }
            if (onCreated) onCreated(created, { issueId: newIssueId });
            setFormData((prev) => ({
              ...prev,
              projectId: "",
              subProjectId: "",
              taskId: "",
              subTaskId: "",
              title: "",
              description: "",
              dueDate: "",
              actualEndDate: "",
              assignedUserIds: [],
              testCaseId: ""
            }));
            return;
          }
        }
      }

      setPendingFiles([]);
      try {
        sessionStorage.removeItem(ISSUE_CREATE_FORM_DRAFT_KEY);
      } catch {
        /* ignore */
      }
      if (onCreated) onCreated(created, { issueId: newIssueId });
      setFormData((prev) => ({
        ...prev,
        projectId: "",
        subProjectId: "",
        taskId: "",
        subTaskId: "",
        title: "",
        description: "",
        dueDate: "",
        actualEndDate: "",
        assignedUserIds: [],
        testCaseId: ""
      }));
    } catch (err) {
      console.error("Error creating issue:", err);
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to create issue.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="issues-form" onSubmit={handleSubmit}>
      <div className="issues-form-title">Create Issue</div>

      {!canCreate ? (
        <div className="issues-alert">
          You don’t have permission to create issues.
        </div>
      ) : null}

      {error ? <div className="issues-alert issues-alert-error">{error}</div> : null}

      <div className="issues-form-grid">
        <div className="form-group">
          <label>Project <span className="required">*</span></label>
          <select
            name="projectId"
            value={formData.projectId}
            onChange={handleInputChange}
            required
            disabled={!canCreate || loadingProjects}
          >
            <option value="">
              {loadingProjects ? "Loading projects..." : "Select project"}
            </option>
            {projects.map((p) => (
              <option
                key={p.Project_Id ?? p.project_id ?? p.id}
                value={String(p.Project_Id ?? p.project_id ?? p.id)}
              >
                {p.Project_Name ?? p.project_name ?? p.name ?? ""}
              </option>
            ))}
          </select>
        </div>

        {showSubprojectField ? (
          <div className="form-group">
            <label>Subproject</label>
            <select
              name="subProjectId"
              value={formData.subProjectId}
              onChange={handleInputChange}
              disabled={!canCreate || loadingSubprojects}
            >
              <option value="">Select subproject </option>
              {subprojects.map((sp) => (
                <option key={sp.id} value={String(sp.id)}>
                  {sp.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="form-group">
          <label>Task <span className="required">*</span></label>
          <select
            name="taskId"
            value={formData.taskId}
            onChange={handleInputChange}
            required
            disabled={!canCreate || loadingTasks || !formData.projectId}
          >
            <option value="">
              {!formData.projectId
                ? "Select project first"
                : loadingTasks
                  ? "Loading tasks..."
                  : "Select Task"}
            </option>
            {tasks.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.title}
              </option>
            ))}
          </select>
        </div>

        {showSubTaskField ? (
          <div className="form-group">
            <label>Sub Task</label>
            <select
              name="subTaskId"
              value={formData.subTaskId}
              onChange={handleInputChange}
              disabled={!canCreate || !formData.taskId || loadingSubtasks}
            >
              <option value="">
                {loadingSubtasks ? "Loading subtasks..." : "Select sub task (optional)"}
              </option>
              {subtasks.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.title}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="form-group">
          <label>Test case</label>
          <select
            name="testCaseId"
            value={formData.testCaseId}
            onChange={handleInputChange}
            disabled={!canCreate || loadingTestCases || !formData.taskId}
          >
            <option value="">
              {!formData.taskId
                ? "Select task first"
                : loadingTestCases
                  ? "Loading test cases..."
                  : testCases.length
                    ? "Select test case"
                    : "No test cases for this task"}
            </option>
            {testCases.map((tc) => (
              <option key={tc.id} value={String(tc.id)}>
                {tc.title}
              </option>
            ))}
          </select>
          {formData.taskId && !loadingTestCases && testCases.length === 0 ? (
            <button
              type="button"
              className="btn-add-lead"
              style={{ marginTop: 10, width: "100%", maxWidth: 280 }}
              onClick={persistIssueDraftAndOpenCreateTestCase}
              disabled={!canCreate}
            >
              Create test case
            </button>
          ) : null}
          <div className="issues-field-hint" style={{ marginTop: 6 }}>One test case only.</div>
        </div>

        <div className="form-group">
          <label>Title <span className="required">*</span></label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Issue title"
            required
            disabled={!canCreate}
          />
        </div>

        <div className="form-group">
          <label>Type</label>
          <select name="type" value={formData.type} onChange={handleInputChange} disabled={!canCreate}>
            {ISSUE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Issue Layer</label>
          <select name="issueLayer" value={formData.issueLayer} onChange={handleInputChange} disabled={!canCreate}>
            {ISSUE_LAYERS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            name="statusId"
            value={formData.statusId}
            onChange={handleInputChange}
            disabled={!canCreate || loadingStatuses}
          >
            {loadingStatuses ? <option value={formData.statusId}>Loading statuses...</option> : null}
            {statusOptions.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Priority</label>
          <select name="priority" value={formData.priority} onChange={handleInputChange} disabled={!canCreate}>
            {ISSUE_PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Due date</label>
          <input
            type="date"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleInputChange}
            disabled={!canCreate}
          />
        </div>

        <div className="form-group">
          <label>Actual end date</label>
          <input
            type="date"
            name="actualEndDate"
            value={formData.actualEndDate}
            onChange={handleInputChange}
            disabled={!canCreate}
          />
        </div>

        <div className="form-group form-group-multi-select">
          <label>Assigned Users</label>
          <div style={{ width: "100%" }}>
            <div
              className="permissions-select-trigger"
              onClick={() => {
                if (!canCreate) return;
                if (!loadingUsers) setShowAssigneesDropdown((prev) => !prev);
              }}
              style={{
                opacity: (!canCreate || loadingUsers) ? 0.6 : 1,
                cursor: (!canCreate || loadingUsers) ? "default" : "pointer"
              }}
            >
              <span className="permissions-select-text">
                {loadingUsers
                  ? "Loading users..."
                  : formData.assignedUserIds.length === 0
                    ? "Select assignees"
                    : `${formData.assignedUserIds.length} user${formData.assignedUserIds.length > 1 ? "s" : ""} selected`}
              </span>
              <FaChevronDown className={`permissions-select-chevron ${showAssigneesDropdown ? "open" : ""}`} />
            </div>

            {showAssigneesDropdown && !loadingUsers && canCreate && (
              <div
                className="permissions-select-panel"
                style={{ maxHeight: 240, overflowY: "auto" }}
                onClick={(e) => e.stopPropagation()}
              >
                <label className="permissions-select-item">
                  <input
                    type="checkbox"
                    checked={allUsersSelected}
                    onChange={handleToggleSelectAllUsers}
                  />
                  <span className="permissions-select-item-label">Select all</span>
                </label>
                {users.map((u) => (
                  <label key={u.id} className="permissions-select-item">
                    <input
                      type="checkbox"
                      checked={formData.assignedUserIds.includes(u.id)}
                      onChange={() => handleUserToggle(u.id)}
                    />
                    <span className="permissions-select-item-label">{u.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group issues-description-full">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            placeholder="Issue description"
            disabled={!canCreate}
          />
        </div>

        <div className="form-group issues-description-full">
          <label>Attachments (optional)</label>
          <input
            type="file"
            className="issue-files-native-input"
            disabled={!canCreate}
            multiple
            onChange={(e) => {
              const next = Array.from(e.target.files || []);
              setPendingFiles((prev) => [...prev, ...next]);
              e.target.value = "";
            }}
          />
          {pendingFiles.length > 0 ? (
            <ul className="issue-files-pending-list">
              {pendingFiles.map((f, idx) => (
                <li key={`${f.name}-${idx}`} className="issue-files-pending-item">
                  <span className="issue-files-pending-name">{f.name}</span>
                  <button
                    type="button"
                    className="issue-files-pending-remove"
                    onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                    disabled={!canCreate}
                    title="Remove"
                  >
                    <FaTimes />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className="issues-form-actions">
        <button type="submit" className="btn-add-lead" disabled={!canCreate || submitting}>
          {submitting ? "Creating..." : "Create Issue"}
        </button>
      </div>
    </form>
  );
}

